import { useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import {
	Activity,
	ArrowDown,
	ArrowUp,
	CalendarDays,
	Clock3,
	House,
	RefreshCw,
	Server,
	Trophy,
	Users,
} from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApiError } from "@/lib/api/errors";
import {
	fetchPublicTrafficRankingSnapshot,
	type PublicTrafficRankingSnapshot,
} from "@/lib/api/traffic-ranking-snapshot";
import { cn, formatBytes } from "@/lib/utils";

type RankingItem = PublicTrafficRankingSnapshot["user_ranking"][number];

const RANKING_MEDALS = ["🥇", "🥈", "🥉"] as const;

const UUID_PATTERN =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function formatDate(
	value: string,
	timezone: string,
	language: string,
	withTime = false,
): string {
	const parsed = new Date(withTime ? value : `${value}T00:00:00Z`);
	if (Number.isNaN(parsed.getTime())) return value;
	try {
		return parsed.toLocaleString(
			language.startsWith("zh") ? "zh-CN" : "en-US",
			{
				timeZone: timezone,
				dateStyle: "medium",
				...(withTime ? { timeStyle: "short" as const } : {}),
			},
		);
	} catch {
		return value;
	}
}

function SummaryMetric({
	label,
	value,
	detail,
	emphasis = false,
}: {
	label: string;
	value: string;
	detail?: string;
	emphasis?: boolean;
}) {
	return (
		<div
			className={cn(
				"border-l-2 px-4 py-1",
				emphasis ? "border-primary" : "border-border",
			)}
		>
			<p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
				{label}
			</p>
			<p className="mt-2 font-mono text-2xl font-semibold tabular-nums sm:text-3xl">
				{value}
			</p>
			{detail && <p className="mt-1 text-xs text-muted-foreground">{detail}</p>}
		</div>
	);
}

function RankBadge({ index }: { index: number }) {
	const position = index + 1;
	const medal = RANKING_MEDALS[index];
	if (medal) {
		return (
			<span className="flex size-9 shrink-0 items-center justify-center text-2xl leading-none">
				<span aria-hidden="true">{medal}</span>
				<span className="sr-only">{position}</span>
			</span>
		);
	}
	return (
		<span className="flex size-9 shrink-0 items-center justify-center rounded-full border border-border bg-background font-mono text-sm font-semibold tabular-nums text-muted-foreground">
			{position}
		</span>
	);
}

function Leaderboard({
	title,
	items,
	icon: Icon,
}: {
	title: string;
	items: RankingItem[];
	icon: typeof Users;
}) {
	const { t } = useTranslation();
	const max = Math.max(
		0,
		...items.map((item) => Number(item.total_bytes) || 0),
	);

	return (
		<section className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-elevation-1">
			<header className="flex items-start justify-between gap-4 border-b border-border/70 px-5 py-5 sm:px-6">
				<div className="flex items-start gap-3">
					<span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary-container text-primary-on-container">
						<Icon className="size-5" aria-hidden="true" />
					</span>
					<div>
						<h2 className="font-semibold tracking-tight">{title}</h2>
					</div>
				</div>
				<span className="rounded-full bg-muted px-3 py-1 font-mono text-xs tabular-nums text-muted-foreground">
					{items.length}
				</span>
			</header>

			{items.length === 0 ? (
				<div className="flex min-h-52 flex-col items-center justify-center px-6 text-center">
					<Activity
						className="size-8 text-muted-foreground/50"
						aria-hidden="true"
					/>
					<p className="mt-3 text-sm font-medium">
						{t("public.ranking.emptyTitle")}
					</p>
					<p className="mt-1 text-xs text-muted-foreground">
						{t("public.ranking.emptyDescription")}
					</p>
				</div>
			) : (
				<ol className="divide-y divide-border/60">
					{items.map((item, index) => {
						const total = Number(item.total_bytes) || 0;
						const upload = Number(item.upload_bytes) || 0;
						const download = Number(item.download_bytes) || 0;
						const width = max > 0 ? Math.max(2, (total / max) * 100) : 0;
						const directionTotal = upload + download;
						const uploadShare =
							directionTotal > 0 ? (upload / directionTotal) * 100 : 100;

						return (
							<li
								key={`${item.name}-${item.total_bytes}-${item.upload_bytes}-${item.download_bytes}`}
								className="relative px-5 py-4 sm:px-6"
							>
								<div className="relative z-10 flex items-center gap-3">
									<RankBadge index={index} />
									<div className="min-w-0 flex-1">
										<div className="flex items-baseline justify-between gap-3">
											<p
												className="truncate text-sm font-medium"
												title={item.name}
											>
												{item.name}
											</p>
											<p className="shrink-0 font-mono text-sm font-semibold tabular-nums">
												{formatBytes(total)}
											</p>
										</div>
										<div className="mt-2 flex items-center gap-3 text-[11px] text-muted-foreground">
											<span className="inline-flex items-center gap-1">
												<ArrowUp
													className="size-3 text-primary"
													aria-hidden="true"
												/>
												{formatBytes(upload)}
											</span>
											<span className="inline-flex items-center gap-1">
												<ArrowDown
													className="size-3 text-tertiary"
													aria-hidden="true"
												/>
												{formatBytes(download)}
											</span>
										</div>
									</div>
								</div>
								<div
									className="absolute inset-x-5 bottom-0 h-1 overflow-hidden rounded-full bg-muted sm:inset-x-6"
									aria-hidden="true"
								>
									<div className="flex h-full" style={{ width: `${width}%` }}>
										<span
											className="h-full bg-primary"
											style={{ width: `${uploadShare}%` }}
										/>
										<span className="h-full flex-1 bg-tertiary" />
									</div>
								</div>
							</li>
						);
					})}
				</ol>
			)}
		</section>
	);
}

function RankingLoading() {
	return (
		<div className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6 sm:py-14">
			<Skeleton className="h-5 w-36" />
			<Skeleton className="mt-5 h-12 w-72 max-w-full" />
			<div className="mt-8 grid gap-4 sm:grid-cols-2">
				<Skeleton className="h-28" />
				<Skeleton className="h-28" />
			</div>
			<div className="mt-8 grid gap-6 md:grid-cols-2">
				<Skeleton className="h-96" />
				<Skeleton className="h-96" />
			</div>
		</div>
	);
}

function RankingError({
	title,
	description,
	onRetry,
	requestId,
}: {
	title: string;
	description: string;
	onRetry?: () => void;
	requestId?: string;
}) {
	const { t } = useTranslation();
	return (
		<div className="mx-auto flex min-h-[68vh] w-full max-w-xl items-center px-4 py-16 sm:px-6">
			<div className="w-full rounded-2xl border border-border/70 bg-card p-7 text-center shadow-elevation-1 sm:p-10">
				<span className="mx-auto flex size-12 items-center justify-center rounded-full bg-warning-container text-warning-on-container">
					<Activity className="size-6" aria-hidden="true" />
				</span>
				<h1 className="mt-5 text-xl font-semibold tracking-tight">{title}</h1>
				<p className="mx-auto mt-2 max-w-md text-sm leading-6 text-muted-foreground">
					{description}
				</p>
				{requestId && (
					<p className="mt-3 font-mono text-xs text-muted-foreground">
						{t("public.ranking.requestId", { id: requestId })}
					</p>
				)}
				<div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
					{onRetry && (
						<Button type="button" onClick={onRetry}>
							<RefreshCw aria-hidden="true" />
							{t("public.ranking.retry")}
						</Button>
					)}
					<Button asChild variant="outline">
						<Link to="/">
							<House aria-hidden="true" />
							{t("public.ranking.home")}
						</Link>
					</Button>
				</div>
			</div>
		</div>
	);
}

export default function TrafficRanking() {
	const { t, i18n } = useTranslation();
	const reducedMotion = useReducedMotion();
	const [searchParams] = useSearchParams();
	const snapshotId = searchParams.get("uuid")?.trim() ?? "";
	const validSnapshotId = UUID_PATTERN.test(snapshotId);
	const language = (i18n.resolvedLanguage ?? i18n.language).toLowerCase();
	useEffect(() => {
		const previousTitle = document.title;
		document.title = `${t("public.ranking.title")} · nextsub`;
		return () => {
			document.title = previousTitle;
		};
	}, [t]);
	const snapshotQuery = useQuery({
		queryKey: ["public-traffic-ranking-snapshot", snapshotId],
		queryFn: () => fetchPublicTrafficRankingSnapshot(snapshotId),
		enabled: validSnapshotId,
		retry: (failureCount, error) =>
			!(error instanceof ApiError && error.status === 404) && failureCount < 1,
		staleTime: Number.POSITIVE_INFINITY,
	});

	let content: React.ReactNode;
	if (!snapshotId) {
		content = (
			<RankingError
				title={t("public.ranking.missingTitle")}
				description={t("public.ranking.missingDescription")}
			/>
		);
	} else if (!validSnapshotId) {
		content = (
			<RankingError
				title={t("public.ranking.invalidTitle")}
				description={t("public.ranking.invalidDescription")}
			/>
		);
	} else if (snapshotQuery.isPending) {
		content = <RankingLoading />;
	} else if (snapshotQuery.isError || !snapshotQuery.data) {
		const apiError =
			snapshotQuery.error instanceof ApiError ? snapshotQuery.error : undefined;
		const missing = apiError?.status === 404;
		content = (
			<RankingError
				title={t(
					missing ? "public.ranking.expiredTitle" : "public.ranking.errorTitle",
				)}
				description={t(
					missing
						? "public.ranking.expiredDescription"
						: "public.ranking.errorDescription",
				)}
				onRetry={missing ? undefined : () => void snapshotQuery.refetch()}
				requestId={apiError?.requestId}
			/>
		);
	} else {
		const snapshot = snapshotQuery.data;
		const dailyDetail = `${t("public.ranking.upload")} ${formatBytes(snapshot.daily.upload_bytes)} · ${t("public.ranking.download")} ${formatBytes(snapshot.daily.download_bytes)}`;
		content = (
			<main className="bg-glyph-grid" data-page="traffic-ranking">
				<section className="border-b border-border/60 bg-background/90">
					<motion.div
						initial={reducedMotion ? false : { opacity: 0, y: 14 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}
						className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14"
					>
						<div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
							<div>
								<div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary-container px-3 py-1 text-xs font-medium text-primary-on-container">
									<Trophy className="size-3.5" aria-hidden="true" />
									{t("public.ranking.badge")}
								</div>
								<h1 className="mt-5 text-3xl font-semibold tracking-[-0.035em] sm:text-4xl">
									{t("public.ranking.title")}
								</h1>
							</div>
							<div className="space-y-2 text-sm text-muted-foreground">
								<p className="flex items-center gap-2">
									<CalendarDays
										className="size-4 text-primary"
										aria-hidden="true"
									/>
									{formatDate(
										snapshot.report_date,
										snapshot.timezone,
										language,
									)}
								</p>
								<p className="flex items-center gap-2 text-xs">
									<Clock3 className="size-4" aria-hidden="true" />
									{t("public.ranking.generatedAt", {
										time: formatDate(
											snapshot.generated_at,
											snapshot.timezone,
											language,
											true,
										),
										zone: t("public.ranking.timezoneHongKong"),
									})}
								</p>
							</div>
						</div>

						<div className="mt-9 grid gap-6 border-t border-border/70 pt-7 sm:grid-cols-2">
							<SummaryMetric
								label={t("public.ranking.dailyTotal")}
								value={formatBytes(snapshot.daily.total_bytes)}
								detail={dailyDetail}
								emphasis
							/>
							<SummaryMetric
								label={t("public.ranking.last30Days")}
								value={formatBytes(snapshot.last_30_days.total_bytes)}
							/>
						</div>
					</motion.div>
				</section>

				<section className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
					<div className="hidden gap-6 md:grid md:grid-cols-2">
						<Leaderboard
							title={t("public.ranking.usersTitle")}
							items={snapshot.user_ranking}
							icon={Users}
						/>
						<Leaderboard
							title={t("public.ranking.nodesTitle")}
							items={snapshot.node_ranking}
							icon={Server}
						/>
					</div>

					<Tabs defaultValue="users" className="md:hidden">
						<TabsList className="grid w-full grid-cols-2">
							<TabsTrigger value="users">
								<Users aria-hidden="true" />
								{t("public.ranking.usersTab")}
							</TabsTrigger>
							<TabsTrigger value="nodes">
								<Server aria-hidden="true" />
								{t("public.ranking.nodesTab")}
							</TabsTrigger>
						</TabsList>
						<TabsContent value="users">
							<Leaderboard
								title={t("public.ranking.usersTitle")}
								items={snapshot.user_ranking}
								icon={Users}
							/>
						</TabsContent>
						<TabsContent value="nodes">
							<Leaderboard
								title={t("public.ranking.nodesTitle")}
								items={snapshot.node_ranking}
								icon={Server}
							/>
						</TabsContent>
					</Tabs>
				</section>
			</main>
		);
	}

	return content;
}
